import { Col, InputNumber, Row, Table } from "antd";
import Users from "entities/Users";
import { get } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsCircleFill } from "react-icons/bs";
import { FaEdit } from "react-icons/fa";
import { MdCheck, MdOutlineDeleteOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  AccountFilters,
  UnlockAction,
  accountStatusColumn,
} from "shared/components/AccountStatus";
import Button from "shared/components/Button";
import Pagination from "shared/components/Pagination";
import Switch from "shared/components/Switch";
import useAccountLock from "shared/hooks/useAccountLock";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { USERS } from "utils/api/graphql/queries/users";
import { SCREENS, USER_TYPES } from "utils/constants";

const UsersTable = ({ data, acceptSignUp, updateParams }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const [pagination, setPagination] = useState({});

  const { t: tCollaborator } = useTranslation(
    NAME_SPACES.PRIVATE.BROKER.COLLABORATOR,
  );
  const [lastname, setLastname] = useState("");
  const [firstname, setFirstname] = useState("");

  const DETAIL = t("DETAIL", { returnObjects: true });
  const LIST = t("LIST", { returnObjects: true });

  const where = useMemo(
    () => ({
      AND: [
        {
          OR: [
            { organization: { id: get(data, "organization.id") } },
            ...get(data, "organization.contracts", [])?.map(
              ({ counterpartyOrganization, subjects }) => ({
                organization: {
                  id:
                    get(counterpartyOrganization, "id") ||
                    get(subjects, "signedWith"),
                },
              }),
            ),
          ],
        },
        {
          OR: [
            { meta: { type: USER_TYPES.BROKER_EMPLOYEE.type } },
            { meta: { type: USER_TYPES.BROKER_REPRESENTATIVE.type } },
            { meta: { type: USER_TYPES.INSURANCE_REPRESENTATIVE.type } },
            { meta: { type: USER_TYPES.INTERNAL_ADVISOR.type } },
            { meta: { type: USER_TYPES.INTERNAL_SUPERVISOR.type } },
          ],
        },
      ],
    }),
    [data],
  );

  const { lockStates, onUnlock, status, setStatus, statusIds, statusOptions } =
    useAccountLock(where);

  const resetPage = () => setPagination((prev) => ({ ...prev, skip: 0 }));

  const hasFilters = Boolean(lastname || firstname || status);

  const userFilter = {
    where,
    like: {
      ...(lastname && { lastname: `%${lastname}%` }),
      ...(firstname && { firstname: `%${firstname}%` }),
    },
    ...(statusIds && { isIn: { id: statusIds } }),
    ...pagination,
  };
  const STATUS = {
    [USER_TYPES.BROKER_REPRESENTATIVE.type]: (
      <div className="d-flex align--center">
        <BsCircleFill color={COLORS.C_FEMALE} />
        <span>{DETAIL.REPRESENTATIVE}</span>
      </div>
    ),
    [USER_TYPES.INSURANCE_REPRESENTATIVE.type]: (
      <div className="d-flex align--center">
        <BsCircleFill color={COLORS.C_FEMALE} />
        <span>{DETAIL.REPRESENTATIVE}</span>
      </div>
    ),
    [USER_TYPES.BROKER_EMPLOYEE.type]: (
      <div className="d-flex align--center">
        <BsCircleFill color={COLORS.C_MALE} />
        <span>{DETAIL.EMPLOYEE}</span>
      </div>
    ),
    [USER_TYPES.INTERNAL_ADVISOR.type]: (
      <div className="d-flex align--center">
        <BsCircleFill color={COLORS.C_FEMALE} />
        <span>{DETAIL.ADVISOR}</span>
      </div>
    ),
    [USER_TYPES.INTERNAL_SUPERVISOR.type]: (
      <div className="d-flex align--center">
        <BsCircleFill color={COLORS.C_MALE} />
        <span>{DETAIL.SUPERVISOR}</span>
      </div>
    ),
  };
  const columns = [
    {
      title: LIST.STATUS,
      dataIndex: "meta",
      render: (record) => STATUS[record?.type] || "-",
    },
    ...accountStatusColumn(
      lockStates,
      tCollaborator("LIST.ACCOUNT_STATUS.TITLE"),
    ),
    {
      title: LIST.CIVILITY,
      dataIndex: "gender",
      render: (gender) => <span className="c-text">{LIST.GENDER[gender]}</span>,
    },
    {
      title: LIST.FIRST_NAME,
      dataIndex: "firstname",
    },
    {
      title: LIST.LAST_NAME,
      dataIndex: "lastname",
    },
    {
      title: LIST.EMAIL,
      dataIndex: "email",
      render: (email) => <span className="c-text">{email}</span>,
    },
    {
      title: LIST.SEE_NEW_PROJECTS,
      dataIndex: "params",
      render: (params, row) => (
        <Switch
          className="button__switch"
          checkedChildren={LIST.YES}
          unCheckedChildren={LIST.NO}
          defaultChecked={get(params, "seeNewProjects", false)}
          onChange={(e, _) =>
            updateParams({
              userId: row?.id,
              params: { ...row?.params, seeNewProjects: e },
            })
          }
        />
      ),
    },
    {
      title: LIST.DAILY_LEAD_QUOTA,
      dataIndex: "params",
      key: "dailyLeadQuota",
      render: (params, row) => (
        <InputNumber
          min={0}
          step={1}
          precision={0}
          placeholder={LIST.UNLIMITED}
          defaultValue={get(params, "dailyLeadQuota", null)}
          onBlur={(e) => {
            const rawValue = get(e, "target.value", "");
            const parsedValue =
              rawValue === "" || rawValue === null
                ? null
                : Number(rawValue);
            const nextValue =
              parsedValue === null || Number.isNaN(parsedValue)
                ? null
                : parsedValue;
            if (nextValue === get(params, "dailyLeadQuota", null)) return;
            updateParams({
              userId: row?.id,
              params: { ...row?.params, dailyLeadQuota: nextValue },
            });
          }}
        />
      ),
    },
    {
      title: LIST.ACCEPT,
      dataIndex: "isActive",
      align: "center",
      render: (isActive, row) => (
        <div className="d-flex justify--center">
          {isActive ? (
            <MdCheck color={COLORS.C_GRAY} />
          ) : (
            <Button
              type="primary"
              onClick={() =>
                acceptSignUp({
                  variables: {
                    data: { baseUrl: process.env.REACT_APP_BASE_URL },
                    where: { id: row.id },
                  },
                  refetchQueries: [
                    {
                      query: USERS,
                      awaitRefetchQueries: true,
                      variables: userFilter,
                    },
                  ],
                })
              }
            >
              <MdCheck />
            </Button>
          )}
        </div>
      ),
    },
    {
      title: LIST.ACTIONS,
      dataIndex: "id",
      align: "right",
      render: (id) => (
        <Row gutter={8} justify="end" align="center" wrap={false}>
          {lockStates?.[id] === true && (
            <Col>
              <UnlockAction onUnlock={() => onUnlock(id)} />
            </Col>
          )}
          <Col
            onClick={() =>
              navigate(`${SCREENS.PRIVATE.ADMIN.USERS.DETAIL.path}/${id}`)
            }
          >
            <FaEdit size={24} color={COLORS.C_PRIMARY} />
          </Col>
          <Col
            onClick={() =>
              navigate(`${SCREENS.PRIVATE.ADMIN.USERS.DEACTIVATE.path}/${id}`)
            }
          >
            <MdOutlineDeleteOutline size={24} color={COLORS.C_DANGER} />
          </Col>
        </Row>
      ),
    },
  ];

  const UsersTable = useCallback(
    ({ data, count }) => {
      return (
        <>
          <Table
            dataSource={data}
            className="table__wrapper"
            columns={columns}
            rowKey={"id"}
            pagination={false}
            scroll={{ x: true }}
            locale={
              hasFilters
                ? { emptyText: tCollaborator("LIST.NO_MATCH") }
                : undefined
            }
          />
          <Pagination
            scrollToTop={false}
            count={count}
            setValues={setPagination}
            skip={pagination.skip || 0}
          />
        </>
      );
    },
    [pagination.skip, lockStates, hasFilters],
  );

  return (
    <>
      <AccountFilters
        onLastnameChange={(value) => {
          resetPage();
          setLastname(value);
        }}
        onFirstnameChange={(value) => {
          resetPage();
          setFirstname(value);
        }}
        status={status}
        onStatusChange={(value) => {
          resetPage();
          setStatus(value);
        }}
        statusOptions={statusOptions}
        showStatus={Boolean(lockStates)}
      />
      <Users
        fetchPolicy="network-only"
        filter={userFilter}
        View={UsersTable}
        NoData={UsersTable}
      />
    </>
  );
};

export default UsersTable;
